import { createFileRoute } from "@tanstack/react-router";
import {
  calendarDayParam,
  enumParam,
  integerParam,
  stringListParam,
  stringParam,
} from "../../../lib/url-state";
import {
  ACTIVITY_VIEWS,
  ANALYTICS_PERIODS,
  EXPLORE_CHARTS,
  OPERATIONS_SPANS,
  PIPELINE_VIEWERS,
  WEEKDAYS,
} from "../../../workspaces/production/activity-search";
import {
  ActivityPage,
  type ActivitySearch,
} from "../../../workspaces/production/pages/ActivityPage";
import { useSeat } from "../../../workspaces/production/seat";

/* A filter is at most what the door takes in one request. */
const FILTER_LIMIT = 50;

export const Route = createFileRoute("/_console/production/activity")({
  validateSearch: (search: Record<string, unknown>): ActivitySearch => ({
    from: calendarDayParam(search.from),
    to: calendarDayParam(search.to),
    accounts: stringParam(search.accounts),
    reportPods: stringParam(search.reportPods),
    collection: stringParam(search.collection),
    episode: stringParam(search.episode),
    rigs: stringParam(search.rigs),
    task: stringParam(search.task),
    view: enumParam(search.view, ACTIVITY_VIEWS),
    // Operations and the Operators lanes: the span, the operator, and the open stretch
    // (an instant inside it); their day is `date`.
    span: enumParam(search.span, OPERATIONS_SPANS),
    operator: stringParam(search.operator),
    at: integerParam(search.at, { min: 0 }),
    // Checks: the floor check open for a verdict.
    check: stringParam(search.check),
    // The pod timeline: one pod, one local calendar day.
    pod: stringParam(search.pod),
    date: calendarDayParam(search.date),
    viewer: enumParam(search.viewer, PIPELINE_VIEWERS),
    period: enumParam(search.period, ANALYTICS_PERIODS),
    since: calendarDayParam(search.since),
    until: calendarDayParam(search.until),
    pods: stringListParam(search.pods, FILTER_LIMIT),
    operators: stringListParam(search.operators, FILTER_LIMIT),
    tasks: stringListParam(search.tasks, FILTER_LIMIT),
    // The cross-filter: a day or an hour of it, a marked slot of the week, and the
    // facets of the episodes behind a number (kept as written; the page says when one
    // is not a value it knows).
    day: calendarDayParam(search.day),
    hour: integerParam(search.hour, { min: 0, max: 23 }),
    weekday: (() => {
      const weekday = enumParam(typeof search.weekday === "number" ? String(search.weekday) : search.weekday, WEEKDAYS);
      return weekday === undefined ? undefined : Number(weekday);
    })(),
    outcome: stringParam(search.outcome),
    cause: stringParam(search.cause),
    detail: stringParam(search.detail),
    stage: stringParam(search.stage),
    // Kept as written: Explore reads them against the door's catalog and says what it
    // cannot answer, rather than the address quietly asking something else.
    measure: stringParam(search.measure),
    by: stringListParam(search.by),
    chart: enumParam(search.chart, EXPLORE_CHARTS),
  }),
  component: ActivityRoute,
});

function ActivityRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  return (
    <ActivityPage
      role={useSeat()}
      search={search}
      onSearchChange={(next) => void navigate({ search: next })}
    />
  );
}
